import pluralize from "pluralize";

import type { AgenticLogicRuleDto } from "@intelligems/ig-types/dtos/agentic-logic/rules";

/** What the rule is busy with; `idle` once nothing is in flight. */
export type RuleOperationPhase = "idle" | "building" | "applying" | "checking";

export const BUSY_HINT = "Wait for the current operation to finish.";
export const WRITING_HINT = "Wait for your changes to finish saving.";

/** Why an action must wait, or `undefined` once nothing is in flight. */
export const waitHint = ({
  isBusy,
  isWriting,
}: {
  isBusy: boolean;
  isWriting: boolean;
}) => (isBusy ? BUSY_HINT : isWriting ? WRITING_HINT : undefined);

/**
 * Whether the run in flight only checks (Run checks) rather than applies. A
 * repair run speaks for its own jobs while it is active; once it has stopped,
 * the current job's mode is the answer, so a finished repair cannot make a
 * checks-only run read as an Apply.
 */
export const isChecksOnlyRun = ({
  repairActive,
  repairPublishesOnSuccess,
  jobMode,
}: {
  repairActive: boolean;
  repairPublishesOnSuccess: boolean | undefined;
  jobMode: "publish" | "dry_run" | undefined;
}) =>
  repairActive ? repairPublishesOnSuccess === false : jobMode === "dry_run";

export interface RuleSaveApplyStateInput {
  ruleState: AgenticLogicRuleDto["state"];
  /** How many experiences use the rule; `null` while that is loading. */
  usedInCount: number | null;
  hasAppliedVersion: boolean;
  /** The saved draft differs from the live version. */
  hasSavedChanges: boolean;
  /** Local edits the autosave has not written yet. */
  isDirty: boolean;
  isSaving: boolean;
  isComparing: boolean;
  comparisonFailed: boolean;
  phase: RuleOperationPhase;
  /**
   * The page opened on a saved draft that already differed from live — a
   * closed tab or a refresh left it unapplied. Cleared by the caller once the
   * draft matches live again.
   */
  openedWithUnappliedDraft: boolean;
  /** When the draft was last saved, already phrased: "just now", "2m ago". */
  savedAgo: string;
  /** Why the pending edits cannot be autosaved (invalid code, a conflict). */
  saveBlockedHint?: string | undefined;
}

export type RuleSaveStatus =
  | { kind: "saving"; text: "Saving…"; tone: "default" }
  | { kind: "saved" | "autosaved" | "live"; text: string; tone: "default" }
  | { kind: "unapplied" | "blocked"; text: string; tone: "warning" };

export const experiencesLabel = (count: number) =>
  pluralize("experience", count, true);

export const ruleLeaveAction = ({
  confirmOnLeave,
  isWriting,
  canSave,
}: {
  confirmOnLeave: boolean;
  isWriting: boolean;
  canSave: boolean;
}) => {
  if (isWriting && !canSave) {
    return "confirm-unsaved" as const;
  }
  if (isWriting) {
    return "save-first" as const;
  }
  if (confirmOnLeave) {
    return "confirm-unapplied" as const;
  }
  return "leave" as const;
};

export const saveAsNewDisabledHint = ({
  isWriting,
  isBusy,
  copyDisabledHint,
}: {
  isWriting: boolean;
  isBusy: boolean;
  copyDisabledHint: string | undefined;
}) => waitHint({ isBusy, isWriting }) ?? copyDisabledHint;

/**
 * Edits autosave to a draft. What the header offers then depends on one
 * question: does any experience use this rule?
 *
 * - None (a new or unused rule): autosave is the whole story. The header shows
 *   Done, which only exits.
 * - One or more: the draft stays unapplied until the merchant applies it.
 *   Discard and an enabled Apply appear once the draft differs from live, and
 *   leaving with unapplied edits asks first.
 *
 * `usage: "unknown"` while the usage join loads: neither story is safe to tell
 * yet, so the header offers nothing rather than flashing the wrong one.
 */
export const ruleSaveApplyState = (input: RuleSaveApplyStateInput) => {
  const usage =
    input.usedInCount === null
      ? ("unknown" as const)
      : input.usedInCount === 0
        ? ("unused" as const)
        : ("used" as const);
  const count = input.usedInCount ?? 0;
  const busy = input.phase !== "idle";
  const isWriting = input.isDirty || input.isSaving;
  const hasPendingChanges = isWriting || input.hasSavedChanges;

  let status: RuleSaveStatus | null;
  if (input.isDirty && !input.isSaving && input.saveBlockedHint) {
    status = { kind: "blocked", text: input.saveBlockedHint, tone: "warning" };
  } else if (isWriting) {
    status = { kind: "saving", text: "Saving…", tone: "default" };
  } else if (usage === "unused") {
    status = {
      kind: "saved",
      text: `Draft saved ${input.savedAgo}`,
      tone: "default",
    };
  } else if (usage === "unknown") {
    status = null;
  } else if (input.hasSavedChanges && input.openedWithUnappliedDraft) {
    status = {
      kind: "unapplied",
      text: `Draft saved · not applied to ${experiencesLabel(count)}`,
      tone: "warning",
    };
  } else if (input.hasSavedChanges) {
    status = {
      kind: "autosaved",
      text: `Draft autosaved ${input.savedAgo}`,
      tone: "default",
    };
  } else {
    status = {
      kind: "live",
      text: `Live in ${experiencesLabel(count)}`,
      tone: "default",
    };
  }

  const discardDisabledHint = waitHint({ isBusy: busy, isWriting });
  let applyDisabledHint: string | undefined;
  if (discardDisabledHint !== undefined) {
    applyDisabledHint = discardDisabledHint;
  } else if (input.isComparing) {
    applyDisabledHint = "Checking which changes are applied.";
  } else if (input.comparisonFailed) {
    applyDisabledHint = "Check the rule status before applying changes.";
  } else if (input.ruleState === "paused") {
    applyDisabledHint = "Turn on this rule before applying changes.";
  } else if (!input.hasSavedChanges && input.hasAppliedVersion) {
    applyDisabledHint = "There are no changes to apply.";
  }

  const showDiscard =
    usage === "used" && input.hasAppliedVersion && hasPendingChanges;

  return {
    usage,
    status,
    hasPendingChanges,
    showDone: usage === "unused",
    showApply: usage === "used",
    isApplying: input.phase === "applying",
    applyLabel:
      input.phase === "applying"
        ? "Running checks…"
        : `Apply to ${experiencesLabel(count)}`,
    applyDisabledHint,
    showDiscard,
    discardDisabledHint,
    isWriting,
    /** Leaving asks "Apply your changes?" only for unapplied edits on a used rule. */
    confirmOnLeave: usage === "used" && hasPendingChanges && !busy,
  };
};

export type RuleSaveApplyState = ReturnType<typeof ruleSaveApplyState>;
